/**
 * Store de RF FORA do React.
 *
 * Regra de performance inegociável (docs/06-frontend-ux-mobile.md §1): S-meter,
 * waterfall e medidores atualizam a 30–60 Hz e matariam a reconciliação do
 * React. Componentes visuais leem daqui por ref + requestAnimationFrame.
 *
 * React continua cuidando de navegação, formulários e telas de configuração —
 * só o caminho quente do rádio fica fora.
 */

import type { BandScope, ChannelState, RXParams } from './protocol'

export interface RFSnapshot {
  connected: boolean
  transmitting: boolean
  freqKHz: number
  mode: string
  channel: ChannelState | null
  rx: RXParams | null
  /** Momento da última atualização de RX, para detectar canal silencioso. */
  rxAt: number
  lastDenial: string | null
  /** Estado da conexão de mídia; null quando o áudio ainda não foi ligado. */
  mediaState: RTCPeerConnectionState | null
  /** Último panorama da faixa, para o waterfall. */
  scope: BandScope | null
  /**
   * Bipe em curso — início ou roger.
   *
   * A interface PRECISA disto. Com bipe de início, o microfone abre depois do
   * tom, e sem aviso a pessoa fala por cima dele e perde a primeira palavra
   * toda vez, sem entender por quê.
   */
  toning: boolean
  /**
   * Modo avançado de CW: o texto já efetivamente TOCADO pelo lado de quem
   * manda — não o que está digitado, o que já saiu no ar. Cresce a cada
   * lote que `ControlPlane` dreno da fila, então `CWSender` consegue
   * mostrar pro próprio operador o que já foi transmitido de verdade.
   */
  cwSent: string
  /**
   * Janela da transmissão em curso — de onde o PTT calcula o contador
   * regressivo. `txStartedAtMs` é capturado no CLIENTE no instante em que
   * `tx_granted` chega, não vem do servidor: evita depender de relógio
   * sincronizado, e o corte de verdade (Hub.EnforceTimeouts) é sempre do
   * servidor mesmo — isto aqui é só o que a interface mostra. `null` fora
   * de uma transmissão.
   */
  txMaxSeconds: number | null
  txStartedAtMs: number | null
}

type Listener = () => void

class RFStore {
  private state: RFSnapshot = {
    connected: false,
    transmitting: false,
    // Valor de partida antes de /api/bands responder. É o canal 11 da PX — o
    // de chamada —, não 7100 SSB: mesmo o primeiro quadro pintado não deve
    // mostrar uma frequência onde a pessoa não pode falar. O valor de verdade
    // vem do servidor logo em seguida.
    freqKHz: 27085,
    mode: 'AM',
    channel: null,
    rx: null,
    rxAt: 0,
    lastDenial: null,
    mediaState: null,
    scope: null,
    toning: false,
    cwSent: '',
    txMaxSeconds: null,
    txStartedAtMs: null,
  }

  private listeners = new Set<Listener>()

  /** Leitura sem alocação, para o laço de rAF. */
  snapshot(): Readonly<RFSnapshot> {
    return this.state
  }

  /**
   * Assinatura para componentes React que precisam reagir a mudanças
   * estruturais (conexão, canal) — nunca para o S-meter.
   */
  subscribe = (fn: Listener): (() => void) => {
    this.listeners.add(fn)
    return () => {
      this.listeners.delete(fn)
    }
  }

  private emit() {
    for (const fn of this.listeners) fn()
  }

  /** Atualiza sem notificar o React: usado no caminho quente a 10 Hz. */
  setRX(rx: RXParams | null) {
    this.state = { ...this.state, rx, rxAt: performance.now() }
  }

  /** Caminho quente: o waterfall lê por rAF, sem notificar o React. */
  setScope(scope: BandScope) {
    this.state = { ...this.state, scope }
  }

  /**
   * Frequência local, aplicada na hora enquanto se arrasta o dial.
   *
   * NÃO notifica o React, pela mesma razão de setRX e setScope: arrastar dispara
   * dezenas de eventos por segundo. Quem mostra a frequência lê daqui num laço
   * de animação.
   *
   * O servidor continua sendo a autoridade — o channel_state seguinte
   * sobrescreve isto. Aqui é só para o dial não parecer emborrachado enquanto a
   * confirmação não volta.
   */
  setFreq(freqKHz: number) {
    this.state = { ...this.state, freqKHz }
  }

  setChannel(channel: ChannelState) {
    this.state = {
      ...this.state,
      channel,
      freqKHz: channel.freq_khz,
      mode: channel.mode,
    }
    this.emit()
  }

  setConnected(connected: boolean) {
    if (this.state.connected === connected) return
    this.state = { ...this.state, connected }
    this.emit()
  }

  setTransmitting(transmitting: boolean) {
    if (this.state.transmitting === transmitting) return
    this.state = {
      ...this.state,
      transmitting,
      // Some junto: sem isto, um contador regressivo velho ficaria parado
      // na tela até a próxima transmissão sobrescrever.
      ...(transmitting ? null : { txMaxSeconds: null, txStartedAtMs: null }),
    }
    this.emit()
  }

  /** Marca o início da janela de transmissão — de onde o PTT calcula o
   * contador regressivo (ver o comentário de txMaxSeconds). */
  setTXGranted(maxSeconds: number) {
    this.state = { ...this.state, txMaxSeconds: maxSeconds, txStartedAtMs: Date.now() }
    this.emit()
  }

  /** Caminho quente: o modo avançado de CW lê por rAF, sem notificar o React. */
  setCWSent(cwSent: string) {
    this.state = { ...this.state, cwSent }
  }

  setToning(toning: boolean) {
    if (this.state.toning === toning) return
    this.state = { ...this.state, toning }
    this.emit()
  }

  setMediaState(mediaState: RTCPeerConnectionState) {
    if (this.state.mediaState === mediaState) return
    this.state = { ...this.state, mediaState }
    this.emit()
  }

  setDenial(reason: string | null) {
    this.state = { ...this.state, lastDenial: reason }
    this.emit()
  }

  /** Considera o canal silencioso se não chega RXParams há mais de 500 ms. */
  isReceiving(now: number): boolean {
    return this.state.rx !== null && now - this.state.rxAt < 500
  }
}

export const rfStore = new RFStore()
